import { Download, FilePenLine, FileText, Link2, Mail, ShieldCheck } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, Badge, Button, ConfirmDialog, EmptyState } from '@payment-plan/ui';
import { useAuth } from '../../../auth/auth-context';
import { ApiError } from '../../../lib/api-client';
import { can } from '../../../lib/permissions';
import type {
  EnlaceDescarga,
  ResultadoIntegridad,
  SeparacionDetalle,
  VersionDocumento,
} from '../../../types/consultas';
import { mensajeDeError } from '../../separations/errors';
import { rutaEditarSeparacion } from '../../separations/rutas';
import { ESTADO_ANULADA, ESTADO_GENERADA } from '../estados';
import { formatHora, formatInstante, huellaCorta } from '../formato';
import type { AlcanceConsulta } from '../use-consultas';
import {
  HUELLA_NO_COINCIDE,
  useDocuments,
  useDownloadVersion,
  useOpenCorrection,
  useVersionIntegrity,
  useVersionLink,
} from '../use-documentos';
import { mensajeDeEnvio, useEnviarCorreo } from '../use-enviar-correo';

const TIPO_PLAN_PAGOS = 'PLAN_PAGOS';
/** Mensajes propios del front que ya están redactados para el usuario (no técnicos). */
const MENSAJES_PROPIOS = new Set([HUELLA_NO_COINCIDE]);

interface DocumentsPanelProps {
  separacion: SeparacionDetalle;
  alcance: AlcanceConsulta;
}

interface HistorialPlan {
  documentoId: string | null;
  versionActualId: string | null;
  versiones: VersionDocumento[];
  /** `true` si el historial sale del detalle porque `/documents` no alcanza la separación (B-01). */
  respaldo: boolean;
}

function mensaje(error: unknown, accion: string): string {
  if (!(error instanceof ApiError) && error instanceof Error && MENSAJES_PROPIOS.has(error.message)) {
    return error.message;
  }
  return mensajeDeError(error, accion);
}

/** Historial desde `detalle.documentos` (forma de feat-28/29) cuando `/documents` no está disponible. */
function historialDelDetalle(separacion: SeparacionDetalle): HistorialPlan {
  const plan = separacion.documentos.find((d) => d.tipo === TIPO_PLAN_PAGOS) ?? separacion.documentos[0];
  return {
    documentoId: plan?.documentoId ?? null,
    versionActualId: plan?.versionActualId ?? null,
    versiones: (plan?.versiones ?? []).map((v) => ({
      versionId: v.versionId,
      version: v.versionNumber,
      consecutivo: v.consecutivo,
      reemplazaAId: null,
      reemplazaConsecutivo: null,
      hashArchivo: v.hashArchivo ?? null,
      generadoPor: null,
      generadoEn: v.generadoEn,
      enviadoEn: v.enviadoEn,
    })),
    respaldo: true,
  };
}

/**
 * Documentos de una separación: historial de versiones (inmutables), descarga verificada por huella,
 * enlace temporal, integridad, corrección con `ConfirmDialog` y estado/acción de envío por correo.
 */
export function DocumentsPanel({ separacion, alcance }: DocumentsPanelProps): React.JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const permisos = user?.permissions;
  const puedeVer = can(permisos, 'documento.ver');
  const puedeDescargar = can(permisos, 'documento.descargar');
  const puedeCorregir = can(permisos, 'separacion.generar', 'documento.generar');

  const documentos = useDocuments(separacion.id, puedeVer);
  const descargar = useDownloadVersion();
  const enlace = useVersionLink();
  const integridad = useVersionIntegrity();
  const corregir = useOpenCorrection(separacion.id);
  const enviar = useEnviarCorreo(separacion.id);

  const [error, setError] = React.useState<string | null>(null);
  const [descargado, setDescargado] = React.useState<string | null>(null);
  const [enviado, setEnviado] = React.useState<string | null>(null);
  const [enlaceCreado, setEnlaceCreado] = React.useState<(EnlaceDescarga & { consecutivo: string }) | null>(null);
  const [integridades, setIntegridades] = React.useState<Record<string, ResultadoIntegridad>>({});
  const [confirmarCorreccion, setConfirmarCorreccion] = React.useState(false);
  const [correccionAbierta, setCorreccionAbierta] = React.useState(false);

  const historial = React.useMemo<HistorialPlan>(() => {
    if (documentos.data) {
      const plan = documentos.data.find((d) => d.tipoDocumento === TIPO_PLAN_PAGOS) ?? documentos.data[0];
      return {
        documentoId: plan?.documentoId ?? null,
        versionActualId: plan?.versionActualId ?? null,
        versiones: plan?.versiones ?? [],
        respaldo: false,
      };
    }
    return historialDelDetalle(separacion);
  }, [documentos.data, separacion]);

  const fueraDeAlcance = documentos.error instanceof ApiError && documentos.error.status === 404;
  const accionesDisponibles = !historial.respaldo || !puedeVer;
  const mostrarCorregir =
    puedeCorregir && separacion.estado === ESTADO_GENERADA && Boolean(historial.documentoId) && accionesDisponibles;

  const inicioAccion = () => {
    setError(null);
    setDescargado(null);
    setEnviado(null);
  };

  return (
    <section aria-labelledby="detalle-documentos" className="animate-rise space-y-4 rounded-xl border border-border/70 bg-card p-5 shadow-elegant sm:p-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 id="detalle-documentos" className="text-lg font-semibold tracking-tight text-foreground">
            Plan de pagos
          </h2>
          <p className="text-sm text-muted-foreground">
            Cada versión emitida es inmutable; una corrección crea una versión nueva con consecutivo propio.
          </p>
        </div>
        {mostrarCorregir && (
          <Button variant="outline" size="lg" className="px-4" onClick={() => setConfirmarCorreccion(true)}>
            <FilePenLine className="h-4 w-4" aria-hidden="true" />
            Corregir plan de pagos
          </Button>
        )}
      </div>

      {puedeVer && fueraDeAlcance && historial.versiones.length > 0 && (
        <Alert variant="info">
          <AlertDescription>
            Esta separación es de otra inmobiliaria: el historial se muestra desde la consulta y la descarga
            no está disponible desde aquí por ahora.
          </AlertDescription>
        </Alert>
      )}

      {puedeVer && documentos.isError && !fueraDeAlcance && (
        <Alert variant="warning">
          <AlertDescription>
            No fue posible consultar los documentos en este momento; se muestra el historial registrado en la
            separación. Recarga la página para ver las acciones.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <p role="status" aria-label="Resultado de la descarga" aria-live="polite" className="text-sm font-medium text-success empty:hidden">
        {descargado}
      </p>
      <p role="status" aria-label="Resultado del envío" aria-live="polite" className="text-sm font-medium text-success empty:hidden">
        {enviado}
      </p>
      <p role="status" aria-label="Resultado de la corrección" aria-live="polite" className="text-sm font-medium text-foreground empty:hidden">
        {correccionAbierta
          ? 'La separación quedó en corrección. Ajusta los datos y genera la nueva versión desde el flujo del asesor.'
          : ''}
      </p>

      {documentos.isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando documentos…</p>
      ) : historial.versiones.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
          title="Aún no hay un plan de pagos emitido"
          description="Se emite desde el flujo del asesor al presionar «Generar plan de pagos»."
          className="py-8"
        />
      ) : (
        <ol aria-label="Versiones del plan de pagos" className="space-y-3">
          {historial.versiones.map((v) => {
            const vigente = v.versionId === historial.versionActualId;
            const verificacion = integridades[v.versionId];
            return (
              <li
                key={v.versionId}
                className={`space-y-3 rounded-lg border p-4 ${vigente ? 'border-brand-gold/60 bg-gold-sheen' : 'border-border/60'}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-foreground">Versión {v.version}</span>
                  <span data-version-consecutivo className="font-mono text-sm tabular-nums text-foreground">
                    {v.consecutivo}
                  </span>
                  {vigente && <Badge variant="success">Vigente</Badge>}
                  {v.reemplazaConsecutivo && (
                    <span className="text-sm text-muted-foreground">Reemplaza a {v.reemplazaConsecutivo}</span>
                  )}
                </div>
                <dl className="grid gap-2 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Emitido</dt>
                    <dd>{formatInstante(v.generadoEn)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Correo</dt>
                    <dd>{v.enviadoEn ? `Enviado el ${formatInstante(v.enviadoEn)}` : 'Sin enviar'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Huella SHA-256</dt>
                    <dd className="font-mono" title={v.hashArchivo ?? undefined}>
                      {huellaCorta(v.hashArchivo)}
                    </dd>
                  </div>
                </dl>

                {accionesDisponibles && (
                  <div className="flex flex-wrap gap-2">
                    {puedeDescargar && (
                      <Button
                        size="lg"
                        className="px-4"
                        loading={descargar.isPending && descargar.variables?.versionId === v.versionId}
                        aria-label={`Descargar PDF ${v.consecutivo}`}
                        onClick={() => {
                          inicioAccion();
                          descargar.mutate(
                            { versionId: v.versionId, huellaEsperada: v.hashArchivo },
                            {
                              onSuccess: (r) =>
                                setDescargado(
                                  `${r.nombreArchivo} descargado · ${
                                    r.verificacion === 'navegador' ? 'huella verificada' : 'verificado por el servidor'
                                  }`,
                                ),
                              onError: (e) => setError(mensaje(e, 'descargar el documento')),
                            },
                          );
                        }}
                      >
                        <Download className="h-4 w-4" aria-hidden="true" />
                        Descargar PDF
                      </Button>
                    )}
                    {puedeDescargar && (
                      <Button
                        variant="outline"
                        size="lg"
                        className="px-4"
                        aria-label={`Enlace temporal ${v.consecutivo}`}
                        onClick={() => {
                          inicioAccion();
                          enlace.mutate(v.versionId, {
                            onSuccess: (r) => setEnlaceCreado({ ...r, consecutivo: v.consecutivo }),
                            onError: (e) => setError(mensaje(e, 'crear el enlace')),
                          });
                        }}
                      >
                        <Link2 className="h-4 w-4" aria-hidden="true" />
                        Enlace temporal
                      </Button>
                    )}
                    {puedeVer && (
                      <Button
                        variant="outline"
                        size="lg"
                        className="px-4"
                        aria-label={`Verificar integridad ${v.consecutivo}`}
                        onClick={() => {
                          inicioAccion();
                          integridad.mutate(v.versionId, {
                            onSuccess: (r) => setIntegridades((prev) => ({ ...prev, [v.versionId]: r })),
                            onError: (e) => setError(mensaje(e, 'verificar el documento')),
                          });
                        }}
                      >
                        <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                        Verificar integridad
                      </Button>
                    )}
                    {alcance === 'propias' && vigente && separacion.estado !== ESTADO_ANULADA && (
                      <Button
                        variant="outline"
                        size="lg"
                        className="px-4"
                        loading={enviar.isPending}
                        aria-label={`Enviar por correo ${v.consecutivo}`}
                        onClick={() => {
                          inicioAccion();
                          enviar.mutate(v.versionId, {
                            onSuccess: (r) => setEnviado(mensajeDeEnvio(r)),
                            onError: (e) => setError(mensaje(e, 'enviar el correo')),
                          });
                        }}
                      >
                        <Mail className="h-4 w-4" aria-hidden="true" />
                        Enviar por correo
                      </Button>
                    )}
                  </div>
                )}

                {verificacion && (
                  <p
                    role="status"
                    aria-live="polite"
                    className={`text-sm font-medium ${verificacion.integro ? 'text-success' : 'text-destructive'}`}
                  >
                    {verificacion.integro
                      ? 'Íntegro: el archivo almacenado coincide con su huella.'
                      : 'No coincide: el archivo almacenado fue alterado. No lo entregues y reporta el caso.'}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {enlaceCreado && (
        <div className="space-y-1 rounded-lg border border-border/70 bg-muted/30 p-4 text-sm">
          <p className="font-medium text-foreground">
            Enlace temporal de {enlaceCreado.consecutivo} · Vence a las {formatHora(enlaceCreado.expiraEn)}
          </p>
          <a
            href={`${window.location.origin}${enlaceCreado.url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block break-all text-brand-gold-dark underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {`${window.location.origin}${enlaceCreado.url}`}
          </a>
          <p className="text-muted-foreground">Cualquiera con el enlace puede descargar el PDF hasta que venza.</p>
        </div>
      )}

      <ConfirmDialog
        open={confirmarCorreccion}
        onOpenChange={setConfirmarCorreccion}
        title="Corregir plan de pagos"
        description="La separación pasará a «Corregida» y podrá editarse. Al generar de nuevo se emitirá una nueva versión con consecutivo propio; la versión actual se conserva en el historial."
        confirmText="Abrir corrección"
        loading={corregir.isPending}
        onConfirm={() => {
          if (!historial.documentoId) return;
          inicioAccion();
          corregir.mutate(historial.documentoId, {
            onSuccess: (r) => {
              setConfirmarCorreccion(false);
              // Integración lote 02: el asesor retoma la separación en el flujo (F-01 de fix-50). La
              // consulta administrativa no navega: el flujo de edición es del asesor dueño.
              if (alcance === 'propias') {
                void navigate(rutaEditarSeparacion(r.separacionId));
                return;
              }
              setCorreccionAbierta(true);
            },
            onError: (e) => {
              setError(mensaje(e, 'abrir la corrección'));
              setConfirmarCorreccion(false);
            },
          });
        }}
      />
    </section>
  );
}
